'use client';

import { c as _c } from "react/compiler-runtime";
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import type { AssetVideo } from '@/generated/graphql';
import type { PreviewableLecture } from '@/types/lecture';
import { usePreviewableAssets } from './use-previewable-assets';
interface CoursePreviewVideoProviderProps {
  children: ReactNode;
}
interface CoursePreviewVideoContextValue {
  previewableItems: PreviewableLecture[];
  selectedId?: string;
  selectedAsset?: AssetVideo;
  setSelectedId: (id: string, callback?: () => void) => void;
}
const CoursePreviewVideoContext = createContext<CoursePreviewVideoContextValue | undefined>(undefined);

/**
 * Provider that manages previewable asset selection for course preview.
 * Uses a simple id→asset map — behaviour is identical for promo video and curriculum lectures.
 */
export function CoursePreviewVideoProvider(t0) {
  const $ = _c(20);
  const {
    children
  } = t0;
  const previewableItems = usePreviewableAssets();
  const [selectedId, setSelectedIdState] = useState(previewableItems[0]?.id);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (id, callback) => {
      setSelectedIdState(id);
      callback?.();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setSelectedId = t1;
  let t2;
  if ($[1] !== previewableItems[0] || $[2] !== previewableItems.length || $[3] !== selectedId) {
    t2 = () => {
      if (previewableItems.length > 0 && !selectedId) {
        setSelectedIdState(previewableItems[0].id);
      }
    };
    $[1] = previewableItems[0];
    $[2] = previewableItems.length;
    $[3] = selectedId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== previewableItems || $[6] !== selectedId) {
    t3 = [previewableItems, selectedId];
    $[5] = previewableItems;
    $[6] = selectedId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  React.useEffect(t2, t3);
  let map;
  if ($[8] !== previewableItems) {
    map = new Map();
    previewableItems.forEach(item => {
      if (item.asset) {
        map.set(item.id, item.asset);
      }
    });
    $[8] = previewableItems;
    $[9] = map;
  } else {
    map = $[9];
  }
  const assetById = map;
  let t4;
  if ($[10] !== assetById || $[11] !== selectedId) {
    t4 = selectedId ? assetById.get(selectedId) : undefined;
    $[10] = assetById;
    $[11] = selectedId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const selectedAsset = t4;
  let t5;
  if ($[13] !== previewableItems || $[14] !== selectedAsset || $[15] !== selectedId) {
    t5 = {
      previewableItems,
      selectedId,
      selectedAsset,
      setSelectedId
    };
    $[13] = previewableItems;
    $[14] = selectedAsset;
    $[15] = selectedId;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const contextValue = t5;
  let t6;
  if ($[17] !== children || $[18] !== contextValue) {
    t6 = <CoursePreviewVideoContext.Provider value={contextValue}>{children}</CoursePreviewVideoContext.Provider>;
    $[17] = children;
    $[18] = contextValue;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}

/**
 * Hook to access the course preview video context — should only be used by the
 * hooks exported from this file to avoid breaking encapsulation.
 */
function useCoursePreviewVideoContext() {
  const context = useContext(CoursePreviewVideoContext);
  if (!context) {
    throw new Error("useCoursePreviewVideoContext must be used within a CoursePreviewVideoProvider");
  }
  return context;
}

/**
 * Hook to set the selected preview item by ID.
 */
export function useSetSelectedPreviewAssetId() {
  const {
    setSelectedId
  } = useCoursePreviewVideoContext();
  return setSelectedId;
}

/**
 * Hook to check if an ID is currently selected for preview.
 */
export function useIsCurrentPreview(id) {
  const {
    selectedId
  } = useCoursePreviewVideoContext();
  return selectedId === id;
}

/**
 * Hook to get the currently selected asset for rendering in the media player.
 */
export function useSelectedPreviewAsset() {
  const {
    selectedAsset
  } = useCoursePreviewVideoContext();
  return selectedAsset;
}

/**
 * Hook to check if we're in a fallback state where no previewable content exists.
 */
export function useIsUsingPromoVideoFallback() {
  const {
    previewableItems
  } = useCoursePreviewVideoContext();
  return previewableItems.length === 0;
}

/**
 * Hook to get all previewable items for display in the preview list.
 * Promo video (if available) appears first, followed by curriculum lectures.
 * The asset field on each item is populated lazily once query resolves.
 */
export function usePreviewableLectures() {
  const {
    previewableItems
  } = useCoursePreviewVideoContext();
  return previewableItems;
}